import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * The right-click menu on a log attribute: filter by it, exclude it, copy it.
 *
 * Fixed to the pointer rather than rendered in flow, because the JSON block
 * scrolls inside a fixed box — a menu positioned in that flow would be clipped
 * by its overflow the moment it opened near the bottom.
 */
export default function AttributeMenu({ x, y, items, onClose }) {
  const ref = useRef(null);
  const [position, setPosition] = useState({ left: x, top: y });

  // Measure, then nudge back inside the viewport. The panel is on the right
  // edge of the screen, so a menu opened there would otherwise hang off it.
  useLayoutEffect(() => {
    const box = ref.current?.getBoundingClientRect();
    if (!box) return;
    const margin = 8;
    setPosition({
      left: Math.max(margin, Math.min(x, window.innerWidth - box.width - margin)),
      top: Math.max(margin, Math.min(y, window.innerHeight - box.height - margin)),
    });
  }, [x, y]);

  useEffect(() => {
    ref.current?.querySelector("button")?.focus();
  }, []);

  useEffect(() => {
    // Capture, like the detail's own Escape handler: the entity panel listens
    // on document and would close itself underneath the menu.
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      event.preventDefault();
      onClose();
    };
    const onAway = (event) => {
      if (!ref.current?.contains(event.target)) onClose();
    };
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("mousedown", onAway, true);
    // Any scroll moves the thing the menu points at, so it stops being true.
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("mousedown", onAway, true);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose]);

  return (
    <div
      className="attr-menu"
      role="menu"
      ref={ref}
      style={{ left: position.left, top: position.top }}
    >
      {items.map((item) => (
        <button
          key={item.label}
          type="button"
          role="menuitem"
          className="attr-menu__item"
          disabled={item.disabled}
          onClick={() => {
            item.onSelect();
            onClose();
          }}
        >
          {item.label}
          {item.hint ? <span className="attr-menu__hint">{item.hint}</span> : null}
        </button>
      ))}
    </div>
  );
}
